/**
 * UIContentEditor Component - Page-Centric Flat Structure
 *
 * Editor for WorkflowContent using page-centric flat dot notation keys.
 * Organization:
 * 1. Info Sheet Page (info_sheet.*)
 * 2. Amount Selection Page (amount_selection.*)
 * 3. Success Page (success.*)
 *
 * Template variables like {{provider_name}} are highlighted in the UI.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Input, TextArea, Select } from '@/components/common/FormField';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { FormFieldsBuilder } from '../FormFieldsBuilder';
import { ActionSectionBuilder } from '../ActionSectionBuilder';
import { ConsentItemsBuilder } from '../ConsentItemsBuilder';
import type {
  WorkflowContent,
  WorkflowType,
  FormFieldDefinition,
  ActionSectionDefinition,
  InfoSheetStep,
  InfoSheetLayout,
} from '@/app/types/rewards';
import { TEMPLATE_VARIABLES } from '@/app/types/rewards';

import './UIContentEditor.scss';

interface UIContentEditorProps {
  value: WorkflowContent | null;
  onChange: (value: WorkflowContent | null) => void;
}

type EditorMode = 'structured' | 'json';
type ActiveSection =
  | 'info_sheet'
  | 'amount_selection'
  | 'success'
  | 'pre_redemption'
  | 'unavailable_info_sheet'
  | 'offer_card';

type LayoutType = 'default' | 'card';

type FieldDefinition = {
  key: string;
  label: string;
  type: 'input' | 'textarea' | 'richtext' | 'array' | 'form_fields' | 'pre_redemption_form_fields' | 'action_section' | 'layout_select' | 'boolean_select' | 'info_sheet_layout_select' | 'info_sheet_steps' | 'consent_items' | 'cta_route_select' | 'pre_redemption_success_layout_select';
  forWorkflow?: 'VARIABLE_AMOUNT' | 'FIXED_AMOUNT' | 'both';
  forLayout?: LayoutType | 'both';
  forInfoSheetLayout?: InfoSheetLayout | 'both';
};

const LAYOUT_OPTIONS = [
  { value: 'default', label: 'Default Layout' },
  { value: 'card', label: 'Card Layout' },
];

const INFO_SHEET_LAYOUT_OPTIONS = [
  { value: 'default', label: 'Default (Prose)' },
  { value: 'stepper', label: 'Stepper (Steps with Icons)' },
];

const PRE_REDEMPTION_SUCCESS_LAYOUT_OPTIONS = [
  { value: 'page', label: 'Full Page' },
  { value: 'sheet', label: 'Bottom Sheet' },
];

const CTA_ROUTE_OPTIONS = [
  { value: '', label: 'None (close sheet only)' },
  { value: '/order-card', label: 'Order Card Page' },
  { value: '/pre-redemption', label: 'Pre-Redemption Page' },
];

const BOOLEAN_OPTIONS = [
  { value: '', label: 'Not Set (use default)' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/**
 * Groups flat keys by their logical page for display
 */
const SECTION_GROUPS = {
  info_sheet: {
    label: 'Info Sheet Page',
    description: 'Content for the initial info sheet that explains the reward.',
    fields: [
      // Common fields (both layouts)
      { key: 'info_sheet.page_title', label: 'Page Title', type: 'input', forInfoSheetLayout: 'both' },
      { key: 'info_sheet.layout', label: 'Layout Type', type: 'info_sheet_layout_select', forInfoSheetLayout: 'both' },

      // Default layout fields (prose)
      { key: 'info_sheet.illustration', label: 'Illustration', type: 'input', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.what_is_this_title', label: 'Section Title: What is this?', type: 'input', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.what_is_this_content', label: 'Content: What is this?', type: 'richtext', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.how_it_works_title', label: 'Section Title: How it works?', type: 'input', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.how_it_works_content_base', label: 'Content: How it works?', type: 'richtext', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.how_it_works_content_minimum', label: 'Content: How it works (Minimum) - Optional', type: 'richtext', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.how_it_works_content_monthly_max', label: 'Content: How it works (Monthly Max) - Optional', type: 'richtext', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.how_it_works_content_yearly_max', label: 'Content: How it works (Yearly Max) - Optional', type: 'richtext', forInfoSheetLayout: 'default' },
      { key: 'info_sheet.how_it_works_content_provider', label: 'Content: How it works (Provider) - Optional', type: 'richtext', forInfoSheetLayout: 'default' },

      // Stepper layout fields
      { key: 'info_sheet.steps', label: 'Stepper Steps', type: 'info_sheet_steps', forInfoSheetLayout: 'stepper' },
      { key: 'info_sheet.show_arrows', label: 'Show Step Arrows', type: 'boolean_select', forInfoSheetLayout: 'stepper' },

      // Consent checkboxes (both layouts, only shown when configured)
      { key: 'info_sheet.consent', label: 'Consent Checkboxes', type: 'consent_items', forInfoSheetLayout: 'both' },

      // Common fields (both layouts)
      { key: 'info_sheet.cta_label', label: 'Primary Action Label', type: 'input', forInfoSheetLayout: 'both' },
    ],
  },
  pre_redemption: {
    label: 'Pre-Redemption Page',
    description: 'Content for pre-redemption data collection (e.g., shipping address for physical cards). If form fields are configured, users will see this page before the standard flow.',
    fields: [
      { key: 'pre_redemption.header_title', label: 'Header Title', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.page_title', label: 'Page Title', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.illustration', label: 'Illustration', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.description', label: 'Description', type: 'richtext', forWorkflow: 'both' },
      { key: 'pre_redemption.form_fields', label: 'Form Fields', type: 'pre_redemption_form_fields', forWorkflow: 'both' },
      { key: 'pre_redemption.cta_label', label: 'Submit Button Label', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.post_submit_available', label: 'Continue to Redemption After Submit', type: 'boolean_select', forWorkflow: 'both' },
      { key: 'pre_redemption.post_submit_blocking_reason', label: 'Blocking Reason (if not continuing)', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.info_sheet_description', label: 'Info Sheet Description (after submission)', type: 'richtext', forWorkflow: 'both' },

      // Success content fields
      { key: 'pre_redemption.success_layout', label: 'Success: Layout', type: 'pre_redemption_success_layout_select', forWorkflow: 'both' },
      { key: 'pre_redemption.success_page_title', label: 'Success: Page Title', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.success_title', label: 'Success: Heading', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.success_illustration', label: 'Success: Illustration', type: 'input', forWorkflow: 'both' },
      { key: 'pre_redemption.success_description', label: 'Success: Description / Next Steps', type: 'richtext', forWorkflow: 'both' },
      { key: 'pre_redemption.success_cta_label', label: 'Success: Button Label', type: 'input', forWorkflow: 'both' },
    ],
  },
  amount_selection: {
    label: 'Amount Selection Page',
    description: 'Content for the amount input and instructions page.',
    fields: [
      // Common fields (both workflow types)
      { key: 'amount_selection.page_title', label: 'Page Title', type: 'input', forWorkflow: 'both' },
      
      // Variable Amount fields
      { key: 'amount_selection.input_label', label: 'Amount Input Label', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.input_placeholder', label: 'Amount Placeholder', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.help_text_base', label: 'Help Text (Base)', type: 'richtext', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.help_text_minimum', label: 'Help Text (Minimum) - Optional', type: 'richtext', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.help_text_monthly_max', label: 'Help Text (Monthly Max) - Optional', type: 'richtext', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.help_text_yearly_max', label: 'Help Text (Yearly Max) - Optional', type: 'richtext', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.validation_required', label: 'Validation: Required', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.validation_min', label: 'Validation: Minimum', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.validation_max_base', label: 'Validation: Maximum (Base)', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.validation_max_monthly_limit', label: 'Validation: Monthly Limit - Optional', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      { key: 'amount_selection.validation_max_annual_limit', label: 'Validation: Annual Limit - Optional', type: 'input', forWorkflow: 'VARIABLE_AMOUNT' },
      
      // Fixed Amount fields
      { key: 'amount_selection.fixed_amount_title', label: 'Fixed: Section Title', type: 'input', forWorkflow: 'FIXED_AMOUNT' },
      { key: 'amount_selection.fixed_amount_label', label: 'Fixed: Amount Label', type: 'input', forWorkflow: 'FIXED_AMOUNT' },
      { key: 'amount_selection.fixed_amount_value', label: 'Fixed: Amount Value (with points)', type: 'input', forWorkflow: 'FIXED_AMOUNT' },
      { key: 'amount_selection.fixed_remaining_label', label: 'Fixed: Remaining Label', type: 'input', forWorkflow: 'FIXED_AMOUNT' },
      { key: 'amount_selection.fixed_description', label: 'Fixed: Description', type: 'richtext', forWorkflow: 'FIXED_AMOUNT' },
      
      // Dynamic form fields (renders BEFORE instructions in MFE)
      { key: 'amount_selection.form_fields', label: 'Dynamic Form Fields', type: 'form_fields', forWorkflow: 'both' },
      
      // Common fields (both workflow types) - instructions come after form fields
      { key: 'amount_selection.instructions_title', label: 'Instructions Title', type: 'input', forWorkflow: 'both' },
      { key: 'amount_selection.instructions_content', label: 'Instructions Content', type: 'richtext', forWorkflow: 'both' },
      { key: 'amount_selection.consent', label: 'Consent Checkboxes', type: 'array', forWorkflow: 'both' },
      { key: 'amount_selection.disclaimer', label: 'Footer Disclaimer', type: 'richtext', forWorkflow: 'both' },
    ],
  },
  success: {
    label: 'Success Page',
    description: 'Content for the confirmation screen shown after redemption.',
    fields: [
      // Common fields (both layouts)
      { key: 'success.page_title', label: 'Page Title', type: 'input', forLayout: 'both' },
      { key: 'success.layout', label: 'Layout Type', type: 'layout_select', forLayout: 'both' },
      { key: 'success.summary_redeemed_label', label: 'Summary: Redeemed Label', type: 'input', forLayout: 'both' },
      { key: 'success.summary_redeemed_value', label: 'Summary: Redeemed Value', type: 'input', forLayout: 'both' },
      { key: 'success.summary_total_points_label', label: 'Summary: Total Points Label', type: 'input', forLayout: 'both' },
      { key: 'success.summary_total_points_value', label: 'Summary: Total Points Value', type: 'input', forLayout: 'both' },
      { key: 'success.illustration_icon', label: 'Illustration Icon', type: 'input', forLayout: 'both' },
      { key: 'success.title', label: 'Title', type: 'input', forLayout: 'both' },
      
      // Card layout specific fields
      { key: 'success.card_description', label: 'Card Description', type: 'richtext', forLayout: 'card' },
      { key: 'success.alert_heading', label: 'Alert: Heading', type: 'input', forLayout: 'card' },
      { key: 'success.alert_message', label: 'Alert: Message', type: 'input', forLayout: 'card' },
      
      // Default layout specific fields
      { key: 'success.main_message', label: 'Main Message', type: 'richtext', forLayout: 'default' },
      { key: 'success.next_steps', label: 'Next Steps', type: 'richtext', forLayout: 'default' },
      
      // Action section (both layouts) - comes after layout-specific content
      { key: 'success.action_section', label: 'Action Section', type: 'action_section', forLayout: 'both' },
      
      // Footer section (both layouts) - replaces simple CTA when present
      { key: 'success.footer_section', label: 'Footer Section', type: 'action_section', forLayout: 'both' },
      
      // CTA button (both layouts) - last item
      { key: 'success.cta_text', label: 'CTA Button Text', type: 'input', forLayout: 'both' },
    ],
  },
  unavailable_info_sheet: {
    label: 'Unavailable Info Sheet',
    description: 'Content for the info sheet shown when a reward is not available (can_redeem=false). If configured, replaces the normal locked info sheet. Leave blank to show the default locked behavior.',
    fields: [
      // Common fields (both layouts)
      { key: 'unavailable_info_sheet.page_title', label: 'Page Title (optional)', type: 'input', forInfoSheetLayout: 'both' },
      { key: 'unavailable_info_sheet.layout', label: 'Layout Type', type: 'info_sheet_layout_select', forInfoSheetLayout: 'both' },

      // Default layout fields (alert banner)
      { key: 'unavailable_info_sheet.alert_title', label: 'Alert Title (optional)', type: 'input', forInfoSheetLayout: 'default' },
      { key: 'unavailable_info_sheet.alert_body', label: 'Alert Body', type: 'richtext', forInfoSheetLayout: 'default' },

      // Stepper layout fields
      { key: 'unavailable_info_sheet.steps', label: 'Stepper Steps', type: 'info_sheet_steps', forInfoSheetLayout: 'stepper' },
      { key: 'unavailable_info_sheet.show_arrows', label: 'Show Step Arrows', type: 'boolean_select', forInfoSheetLayout: 'stepper' },

      // Common fields (both layouts)
      { key: 'unavailable_info_sheet.cta_label', label: 'CTA Label (action needed)', type: 'input', forInfoSheetLayout: 'both' },
      { key: 'unavailable_info_sheet.cta_route', label: 'CTA Navigation Target (action needed)', type: 'cta_route_select', forInfoSheetLayout: 'both' },
      { key: 'unavailable_info_sheet.cta_label_submitted', label: 'CTA Label (already submitted)', type: 'input', forInfoSheetLayout: 'both' },
      { key: 'unavailable_info_sheet.cta_route_submitted', label: 'CTA Navigation Target (already submitted)', type: 'cta_route_select', forInfoSheetLayout: 'both' },
    ],
  },
  offer_card: {
    label: 'Offer Card',
    description: 'Status labels shown on the offer card for available vs unavailable redemption.',
    fields: [
      { key: 'offer_card.available_label', label: 'Available for Redemption Message', type: 'input' },
      { key: 'offer_card.unavailable_label', label: 'Unavailable for Redemption Message', type: 'input' },
    ],
  },
} as const;

export const UIContentEditor: React.FC<UIContentEditorProps> = ({ value, onChange }) => {
  const [mode, setMode] = useState<EditorMode>('structured');
  const [activeSection, setActiveSection] = useState<ActiveSection>('info_sheet');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showTemplateHelper, setShowTemplateHelper] = useState(false);

  // Determine workflow type
  const workflowType: WorkflowType = value?.workflow_type || 'VARIABLE_AMOUNT';
  
  // Determine layout type for success page field filtering
  const layoutType: LayoutType = (value?.['success.layout'] as LayoutType) || 'default';

  // Determine info sheet layout type
  const infoSheetLayoutType: InfoSheetLayout = (value?.['info_sheet.layout'] as InfoSheetLayout) || 'default';

  // Determine unavailable info sheet layout type
  const unavailableInfoSheetLayoutType: InfoSheetLayout = (value?.['unavailable_info_sheet.layout'] as InfoSheetLayout) || 'default';

  // Initialize JSON text when value changes
  useEffect(() => {
    if (mode === 'json') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '{ "workflow_type": "VARIABLE_AMOUNT" }');
    }
  }, [value, mode]);

  // Handle mode toggle
  const handleModeChange = (newMode: EditorMode) => {
    if (newMode === 'json') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '{ "workflow_type": "VARIABLE_AMOUNT" }');
      setJsonError(null);
    }
    setMode(newMode);
    setShowTemplateHelper(false);
  };

  // Handle JSON text changes
  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed as WorkflowContent);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  // Update a flat field value
  const updateField = useCallback(
    (key: string, newValue: string | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (newValue === undefined || newValue === '') {
        delete (updated as Record<string, unknown>)[key];
      } else {
        (updated as Record<string, unknown>)[key] = newValue;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update a boolean field value
  const updateBooleanField = useCallback(
    (key: string, newValue: boolean | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (newValue === undefined) {
        delete (updated as Record<string, unknown>)[key];
      } else {
        (updated as Record<string, unknown>)[key] = newValue;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update an array field
  const updateArrayField = useCallback(
    (key: string, text: string) => {
      const updated = { ...value } as WorkflowContent;
      if (!text.trim()) {
        delete (updated as Record<string, unknown>)[key];
      } else {
        (updated as Record<string, unknown>)[key] = text.split('\n').filter(Boolean);
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update form fields (amount selection)
  const updateFormFields = useCallback(
    (fields: FormFieldDefinition[] | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (!fields || fields.length === 0) {
        delete (updated as Record<string, unknown>)['amount_selection.form_fields'];
      } else {
        (updated as Record<string, unknown>)['amount_selection.form_fields'] = fields;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update pre-redemption form fields
  const updatePreRedemptionFormFields = useCallback(
    (fields: FormFieldDefinition[] | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (!fields || fields.length === 0) {
        delete (updated as Record<string, unknown>)['pre_redemption.form_fields'];
      } else {
        (updated as Record<string, unknown>)['pre_redemption.form_fields'] = fields;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update action section
  const updateActionSection = useCallback(
    (section: ActionSectionDefinition | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (!section) {
        delete (updated as Record<string, unknown>)['success.action_section'];
      } else {
        (updated as Record<string, unknown>)['success.action_section'] = section;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update footer section
  const updateFooterSection = useCallback(
    (section: ActionSectionDefinition | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (!section) {
        delete (updated as Record<string, unknown>)['success.footer_section'];
      } else {
        (updated as Record<string, unknown>)['success.footer_section'] = section;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update steps for any section (info_sheet.steps or unavailable_info_sheet.steps)
  const updateStepsField = useCallback(
    (key: string, steps: InfoSheetStep[] | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (!steps || steps.length === 0) {
        delete (updated as Record<string, unknown>)[key];
      } else {
        (updated as Record<string, unknown>)[key] = steps;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update consent items for a given key
  const updateConsentItems = useCallback(
    (key: string, items: string[] | undefined) => {
      const updated = { ...value } as WorkflowContent;
      if (!items || items.length === 0) {
        delete (updated as Record<string, unknown>)[key];
      } else {
        (updated as Record<string, unknown>)[key] = items;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  // Update workflow type
  const updateWorkflowType = (newType: WorkflowType) => {
    const updated = { ...value, workflow_type: newType } as WorkflowContent;
    onChange(updated);
  };

  // Get a value from the flat structure
  const getValue = (key: string): string => {
    if (!value) {
      return '';
    }
    const val = (value as Record<string, unknown>)[key];
    if (val === undefined || val === null) return '';
    if (Array.isArray(val)) return val.join('\n');
    return String(val);
  };

  const SECTIONS: { key: ActiveSection; label: string }[] = [
    { key: 'info_sheet', label: 'Info Sheet Page' },
    { key: 'unavailable_info_sheet', label: 'Unavailable Info Sheet' },
    { key: 'pre_redemption', label: 'Pre-Redemption Page' },
    { key: 'amount_selection', label: 'Amount Selection Page' },
    { key: 'success', label: 'Success Page' },
    { key: 'offer_card', label: 'Offer Card' },
  ];

  // Render a field based on its type
  const renderField = (field: FieldDefinition) => {
    if (field.type === 'form_fields') {
      const formFields = value?.['amount_selection.form_fields'] as FormFieldDefinition[] | undefined;
      return (
        <FormFieldsBuilder
          key={field.key}
          value={formFields}
          onChange={updateFormFields}
        />
      );
    }

    if (field.type === 'pre_redemption_form_fields') {
      const formFields = value?.['pre_redemption.form_fields'] as FormFieldDefinition[] | undefined;
      return (
        <FormFieldsBuilder
          key={field.key}
          value={formFields}
          onChange={updatePreRedemptionFormFields}
        />
      );
    }

    if (field.type === 'action_section') {
      if (field.key === 'success.footer_section') {
        const footerSection = value?.['success.footer_section'] as ActionSectionDefinition | undefined;
        return (
          <ActionSectionBuilder
            key={field.key}
            value={footerSection}
            onChange={updateFooterSection}
            title="Footer Section"
            description="Configure the fixed footer area at the bottom of the success screen. When enabled, this replaces the simple CTA button. Supports optional instructional text and multiple action buttons (e.g., 'Take me to LoreNote!' + 'I'll do this later')."
            toggleLabel="Enable Footer Section"
          />
        );
      }
      const actionSection = value?.['success.action_section'] as ActionSectionDefinition | undefined;
      return (
        <ActionSectionBuilder
          key={field.key}
          value={actionSection}
          onChange={updateActionSection}
          title="Scrollable Action Section"
          description="Configure an optional action section within the scrollable content area. Buttons can reference URLs from the redemption response (e.g., provider_url)."
          toggleLabel="Enable Action Section"
        />
      );
    }

    if (field.type === 'layout_select') {
      return (
        <Select
          key={field.key}
          label={field.label}
          value={getValue(field.key) || 'default'}
          onChange={(val) => updateField(field.key, val || undefined)}
          options={LAYOUT_OPTIONS}
        />
      );
    }

    if (field.type === 'info_sheet_layout_select') {
      return (
        <Select
          key={field.key}
          label={field.label}
          value={getValue(field.key) || 'default'}
          onChange={(val) => updateField(field.key, val || undefined)}
          options={INFO_SHEET_LAYOUT_OPTIONS}
        />
      );
    }

    if (field.type === 'cta_route_select') {
      return (
        <Select
          key={field.key}
          label={field.label}
          value={getValue(field.key)}
          onChange={(val) => updateField(field.key, val || undefined)}
          options={CTA_ROUTE_OPTIONS}
        />
      );
    }

    if (field.type === 'pre_redemption_success_layout_select') {
      return (
        <Select
          key={field.key}
          label={field.label}
          value={getValue(field.key) || 'page'}
          onChange={(val) => updateField(field.key, val || undefined)}
          options={PRE_REDEMPTION_SUCCESS_LAYOUT_OPTIONS}
        />
      );
    }

    if (field.type === 'info_sheet_steps') {
      const steps = ((value as Record<string, unknown>)?.[field.key] as InfoSheetStep[] | undefined) || [];
      return (
        <div key={field.key} className="info-sheet-steps-builder">
          <label className="info-sheet-steps-builder__label">{field.label}</label>
          <p className="info-sheet-steps-builder__hint">
            Configure each step with an icon filename, title, and description.
          </p>
          {steps.map((step, index) => (
            <div key={index} className="info-sheet-steps-builder__step">
              <div className="info-sheet-steps-builder__step-header">
                <span className="info-sheet-steps-builder__step-number">Step {index + 1}</span>
                <button
                  type="button"
                  className="info-sheet-steps-builder__remove-btn"
                  onClick={() => {
                    const updated = steps.filter((_, i) => i !== index);
                    updateStepsField(field.key, updated.length > 0 ? updated : undefined);
                  }}
                >
                  Remove
                </button>
              </div>
              <Input
                label="Icon Filename"
                value={step.icon}
                onChange={(val) => {
                  const updated = [...steps];
                  updated[index] = { icon: val, title: step.title, description: step.description };
                  updateStepsField(field.key, updated);
                }}
                placeholder="e.g., info-step-smart-button.svg"
              />
              <Input
                label="Title"
                value={step.title}
                onChange={(val) => {
                  const updated = [...steps];
                  updated[index] = { icon: step.icon, title: val, description: step.description };
                  updateStepsField(field.key, updated);
                }}
                placeholder="e.g., Redeem"
              />
              <RichTextEditor
                label="Description"
                value={step.description}
                onChange={(html) => {
                  const updated = [...steps];
                  updated[index] = { icon: step.icon, title: step.title, description: html || '' };
                  updateStepsField(field.key, updated);
                }}
                placeholder="e.g., Select the redeem button below"
              />
            </div>
          ))}
          <button
            type="button"
            className="info-sheet-steps-builder__add-btn"
            onClick={() => {
              const newStep: InfoSheetStep = { icon: '', title: '', description: '' };
              updateStepsField(field.key, [...steps, newStep]);
            }}
          >
            + Add Step
          </button>
        </div>
      );
    }

    if (field.type === 'consent_items') {
      const items = (value as Record<string, unknown>)?.[field.key] as string[] | undefined;
      return (
        <ConsentItemsBuilder
          key={field.key}
          value={items}
          onChange={(newItems) => updateConsentItems(field.key, newItems)}
        />
      );
    }

    if (field.type === 'boolean_select') {
      // Convert boolean value to string for select display
      const currentValue = (value as Record<string, unknown>)?.[field.key];
      const stringValue = currentValue === true ? 'true' : currentValue === false ? 'false' : '';
      return (
        <Select
          key={field.key}
          label={field.label}
          value={stringValue}
          onChange={(val) => {
            // Convert string back to boolean or undefined
            if (val === 'true') updateBooleanField(field.key, true);
            else if (val === 'false') updateBooleanField(field.key, false);
            else updateBooleanField(field.key, undefined);
          }}
          options={BOOLEAN_OPTIONS}
        />
      );
    }

    if (field.type === 'array') {
      return (
        <TextArea
          key={field.key}
          label={field.label}
          value={getValue(field.key)}
          onChange={(val) => updateArrayField(field.key, val)}
          placeholder="One item per line"
          rows={4}
        />
      );
    }
    
    if (field.type === 'richtext') {
      return (
        <RichTextEditor
          key={field.key}
          label={field.label}
          value={getValue(field.key)}
          onChange={(html) => updateField(field.key, html || undefined)}
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      );
    }
    
    if (field.type === 'textarea') {
      return (
        <TextArea
          key={field.key}
          label={field.label}
          value={getValue(field.key)}
          onChange={(val) => updateField(field.key, val || undefined)}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          rows={3}
        />
      );
    }

    return (
      <Input
        key={field.key}
        label={field.label}
        value={getValue(field.key)}
        onChange={(val) => updateField(field.key, val || undefined)}
        placeholder={`Enter ${field.label.toLowerCase()}`}
      />
    );
  };

  return (
    <div className="ui-content-editor">
      {/* Workflow Type Selector */}
      <div className="workflow-type-selector">
        <label>Workflow Type</label>
        <div className="workflow-type-options">
          <button
            type="button"
            className={`type-btn ${workflowType === 'VARIABLE_AMOUNT' ? 'active' : ''}`}
            onClick={() => updateWorkflowType('VARIABLE_AMOUNT')}
          >
            Variable Amount
          </button>
          <button
            type="button"
            className={`type-btn ${workflowType === 'FIXED_AMOUNT' ? 'active' : ''}`}
            onClick={() => updateWorkflowType('FIXED_AMOUNT')}
          >
            Fixed Amount
          </button>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="mode-toggle">
        <button
          type="button"
          className={`toggle-btn ${mode === 'structured' ? 'active' : ''}`}
          onClick={() => handleModeChange('structured')}
        >
          Structured Form
        </button>
        <button
          type="button"
          className={`toggle-btn ${mode === 'json' ? 'active' : ''}`}
          onClick={() => handleModeChange('json')}
        >
          JSON Editor
        </button>
        <button
          type="button"
          className={`toggle-btn helper-btn ${showTemplateHelper ? 'active' : ''}`}
          onClick={() => setShowTemplateHelper(!showTemplateHelper)}
        >
          Template Variables
        </button>
      </div>

      {/* Template Variable Helper */}
      {showTemplateHelper && (
        <div className="template-helper">
          <h5>Available Template Variables</h5>
          <p className="helper-hint">
            Use these placeholders in your content. They will be replaced with actual values at runtime.
          </p>
          <div className="template-list">
            {TEMPLATE_VARIABLES.map((tv) => (
              <div key={tv.variable} className="template-item">
                <code className="template-var">{tv.variable}</code>
                <span className="template-desc">{tv.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'json' ? (
        /* JSON Editor Mode */
        <div className="json-editor">
          <textarea
            className={`json-textarea ${jsonError ? 'error' : ''}`}
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            spellCheck={false}
          />
          {jsonError && <div className="json-error">{jsonError}</div>}
        </div>
      ) : (
        /* Structured Form Mode */
        <div className="structured-editor">
          {/* Section Tabs */}
          <div className="section-tabs">
            {SECTIONS.map((section) => (
              <button
                key={section.key}
                type="button"
                className={`section-tab ${activeSection === section.key ? 'active' : ''}`}
                onClick={() => setActiveSection(section.key)}
              >
                {section.label}
              </button>
            ))}
          </div>

          {/* Section Content */}
          <div className="section-content">
            <div className="form-section">
              <h4>{SECTION_GROUPS[activeSection].label}</h4>
              <p className="section-hint">{SECTION_GROUPS[activeSection].description}</p>
              <div className="form-fields">
                {(SECTION_GROUPS[activeSection].fields as readonly FieldDefinition[])
                  .filter((field: FieldDefinition) => {
                    // Filter fields based on workflow type
                    if (field.forWorkflow && field.forWorkflow !== 'both') {
                      if (field.forWorkflow !== workflowType) return false;
                    }
                    
                    // Filter fields based on layout type (for success page)
                    if (activeSection === 'success' && field.forLayout && field.forLayout !== 'both') {
                      if (field.forLayout !== layoutType) return false;
                    }

                    // Filter fields based on info sheet layout type
                    if (activeSection === 'info_sheet' && field.forInfoSheetLayout && field.forInfoSheetLayout !== 'both') {
                      if (field.forInfoSheetLayout !== infoSheetLayoutType) return false;
                    }

                    // Filter fields based on unavailable info sheet layout type
                    if (activeSection === 'unavailable_info_sheet' && field.forInfoSheetLayout && field.forInfoSheetLayout !== 'both') {
                      if (field.forInfoSheetLayout !== unavailableInfoSheetLayoutType) return false;
                    }
                    
                    return true;
                  })
                  .map(renderField)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UIContentEditor;
